import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Clock, Calendar, X } from "lucide-react";

export interface DateTimeRange {
	from?: Date;
	to?: Date;
}

export interface DateRange {
	from?: Date;
	to?: Date;
}

interface DateTimeRangePickerProps {
	value: DateTimeRange;
	onChange: (range: DateTimeRange) => void;
	placeholder?: string;
	label?: string;
	className?: string;
	mode?: "date" | "datetime";
}

export function DateTimeRangePicker({
	value,
	onChange,
	placeholder = "Chọn khoảng thời gian",
	label,
	className = "",
	mode = "datetime",
}: DateTimeRangePickerProps) {
	const [isOpen, setIsOpen] = useState(false);

	const formatDateTime = (date: Date | undefined): string => {
		if (!date) return "";
		if (mode === "date") {
			return new Intl.DateTimeFormat("vi-VN", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
			}).format(date);
		}
		return new Intl.DateTimeFormat("vi-VN", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
		}).format(date);
	};

	const handleFromChange = (type: "date" | "time", e: React.ChangeEvent<HTMLInputElement>) => {
		let newDate: Date | undefined = value.from ? new Date(value.from) : new Date();

		if (type === "date") {
			const dateValue = e.target.value;
			if (dateValue) {
				const [year, month, day] = dateValue.split("-").map(Number);
				newDate = newDate || new Date();
				newDate.setFullYear(year, month - 1, day);
			} else {
				newDate = undefined;
			}
		} else if (type === "time") {
			const timeValue = e.target.value;
			if (timeValue && newDate) {
				const [hours, minutes] = timeValue.split(":").map(Number);
				newDate.setHours(hours, minutes, 0, 0);
			}
		}

		onChange({ ...value, from: newDate });
	};

	const handleToChange = (type: "date" | "time", e: React.ChangeEvent<HTMLInputElement>) => {
		let newDate: Date | undefined = value.to ? new Date(value.to) : new Date();

		if (type === "date") {
			const dateValue = e.target.value;
			if (dateValue) {
				const [year, month, day] = dateValue.split("-").map(Number);
				newDate = newDate || new Date();
				newDate.setFullYear(year, month - 1, day);
			} else {
				newDate = undefined;
			}
		} else if (type === "time") {
			const timeValue = e.target.value;
			if (timeValue && newDate) {
				const [hours, minutes] = timeValue.split(":").map(Number);
				newDate.setHours(hours, minutes, 0, 0);
			}
		}

		onChange({ ...value, to: newDate });
	};

	const handleClear = () => {
		onChange({ from: undefined, to: undefined });
	};

	const getDisplayText = (): string => {
		if (value.from && value.to) {
			return `${formatDateTime(value.from)} - ${formatDateTime(value.to)}`;
		}
		if (value.from) {
			return `Từ ${formatDateTime(value.from)}`;
		}
		if (value.to) {
			return `Đến ${formatDateTime(value.to)}`;
		}
		return placeholder;
	};

	const hasValue = value.from || value.to;

	return (
		<div className={`relative ${className}`}>
			{label && (
				<label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>
			)}
			<div className="relative">
				<Button
					variant="outline"
					size="sm"
					className="w-full justify-start text-left font-normal h-9 px-2 text-xs"
					onClick={() => setIsOpen(!isOpen)}
				>
					{mode === "date" ? (
						<Calendar className="mr-1 h-3 w-3" />
					) : (
						<Clock className="mr-1 h-3 w-3" />
					)}
					<span className="truncate">{getDisplayText()}</span>
				</Button>
				{hasValue && (
					<Button
						variant="ghost"
						size="sm"
						className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-accent"
						onClick={(e) => {
							e.stopPropagation();
							handleClear();
						}}
					>
						<X className="h-2 w-2" />
					</Button>
				)}
			</div>

			{isOpen && (
				<>
					<div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
					<div
						id="DateTimePickerBackground"
						className="absolute top-full left-0 z-50 mt-1 bg-card border border-border rounded-md shadow-lg p-3 min-w-96 w-max"
					>
						<div className="space-y-3">
							{mode === "datetime" ? (
								<div className="grid grid-cols-4 gap-2 items-end">
									<div>
										<label className="text-xs text-muted-foreground block mb-1">Từ ngày:</label>
										<input
											type="date"
											value={value.from ? value.from.toISOString().split("T")[0] : ""}
											onChange={(e) => handleFromChange("date", e)}
											className="w-full px-2 py-1 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring"
										/>
									</div>
									<div>
										<label className="text-xs text-muted-foreground block mb-1">Giờ:</label>
										<input
											type="time"
											value={
												value.from
													? `${value.from.getHours().toString().padStart(2, "0")}:${value.from.getMinutes().toString().padStart(2, "0")}`
													: ""
											}
											onChange={(e) => handleFromChange("time", e)}
											className="w-full px-2 py-1 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring"
										/>
									</div>
									<div>
										<label className="text-xs text-muted-foreground block mb-1">Đến ngày:</label>
										<input
											type="date"
											value={value.to ? value.to.toISOString().split("T")[0] : ""}
											onChange={(e) => handleToChange("date", e)}
											className="w-full px-2 py-1 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring"
										/>
									</div>
									<div>
										<label className="text-xs text-muted-foreground block mb-1">Giờ:</label>
										<input
											type="time"
											value={
												value.to
													? `${value.to.getHours().toString().padStart(2, "0")}:${value.to.getMinutes().toString().padStart(2, "0")}`
													: ""
											}
											onChange={(e) => handleToChange("time", e)}
											className="w-full px-2 py-1 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring"
										/>
									</div>
								</div>
							) : (
								<div className="flex gap-2 items-end">
									<div className="flex-1">
										<label className="text-xs text-muted-foreground block mb-1">Từ:</label>
										<input
											type="date"
											value={value.from ? value.from.toISOString().split("T")[0] : ""}
											onChange={(e) => handleFromChange("date", e)}
											className="w-full px-2 py-1 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring"
										/>
									</div>
									<div className="flex-1">
										<label className="text-xs text-muted-foreground block mb-1">Đến:</label>
										<input
											type="date"
											value={value.to ? value.to.toISOString().split("T")[0] : ""}
											onChange={(e) => handleToChange("date", e)}
											className="w-full px-2 py-1 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-ring"
										/>
									</div>
								</div>
							)}
							<div className="flex justify-end gap-1">
								<Button
									variant="outline"
									size="sm"
									className="h-7 px-2 text-xs"
									onClick={handleClear}
								>
									Xóa
								</Button>
								<Button
									variant="default"
									size="sm"
									className="h-7 px-2 text-xs"
									onClick={() => setIsOpen(false)}
								>
									OK
								</Button>
							</div>
						</div>
					</div>
				</>
			)}
		</div>
	);
}

export function DateRangePicker({
	value,
	onChange,
	placeholder = "Chọn khoảng thời gian",
	label,
	className = "",
}: {
	value: DateRange;
	onChange: (range: DateRange) => void;
	placeholder?: string;
	label?: string;
	className?: string;
}) {
	return (
		<DateTimeRangePicker
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			label={label}
			className={className}
			mode="date"
		/>
	);
}
